import React, { useContext, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FormField, Input } from "../design-system/components/inputs";
import { AuthNetworkErrorState } from "../design-system/components/layouts";
import { EyeIcon, EyeOffIcon } from "../design-system/icons";
import { typography, borders, textColors, interactive } from "../design-system/tokens";
import { GoogleAuthButton } from "../core/auth/google";
import AuthLayout from "../core/auth/AuthLayout";
import { AuthContext, writeAuthProfileCache } from "../core/context/AuthContext";
import AffiliatePrimaryButton from "./components/AffiliatePrimaryButton";
import AffiliatePortalBlockedNotice from "./components/AffiliatePortalBlockedNotice";
import { AffiliateAuthContext } from "./context/AffiliateAuthContext";
import { affiliateCheck, affiliateLogin } from "./utils/affiliateApi";
import {
  AFFILIATE_PORTAL_BLOCKED_CODE,
  getAffiliatePortalBlockedMessage,
  isAffiliatePortalBlockedError,
} from "./utils/affiliatePortalMessages";
import {
  affiliateBrandGradientText,
  affiliateNavbarGradient,
} from "./utils/uiClasses";
import { usePortalBasePath } from "./hooks/usePortalBasePath";
import { PARTNER_BASE_PATH } from "./utils/portalKind";

const AffiliateLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Same component serves /affiliate/login and /partner/login; the path decides the copy.
  const basePath = usePortalBasePath();
  const isPartnerPath = basePath === PARTNER_BASE_PATH;
  const portalName = isPartnerPath ? "Partner Portal" : "Affiliate Portal";
  const dashboardPath = `${basePath}/dashboard/coupons`;
  const { setAuth } = useContext(AuthContext);
  const { affiliateAuth, refreshAffiliateAuth } = useContext(AffiliateAuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  // The Google callback redirects back here with an error in router state
  // (e.g. "This account is not an affiliate.").
  const [error, setError] = useState(() => location.state?.error || "");
  const [portalBlockedMessage, setPortalBlockedMessage] = useState(() => {
    const fromState = location.state?.error || "";
    const fromCode = location.state?.code;
    if (fromCode === AFFILIATE_PORTAL_BLOCKED_CODE || /currently unavailable for this account/i.test(fromState)) {
      return fromState;
    }
    return "";
  });
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const blockedMessage =
    portalBlockedMessage ||
    affiliateAuth?.portalBlockedMessage ||
    (affiliateAuth?.isPortalBlocked ? getAffiliatePortalBlockedMessage() : "");
  const showPortalBlocked = !!blockedMessage;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setPortalBlockedMessage("");
    setEmailError("");
    setPasswordError("");
    setIsNetworkError(false);

    const trimmedEmail = (email || "").trim();
    const trimmedPassword = (password || "").trim();
    let hasValidationError = false;
    if (!trimmedEmail) {
      setEmailError("Email or username is required");
      hasValidationError = true;
    }
    if (!trimmedPassword) {
      setPasswordError("Password is required");
      hasValidationError = true;
    }
    if (hasValidationError) return;

    setIsSubmitting(true);
    try {
      const loginData = await affiliateLogin({ email: trimmedEmail, password: trimmedPassword });
      // Ensure affiliate role
      const checkData = await affiliateCheck();
      await refreshAffiliateAuth();

      const mustCompleteOnboarding = !!(
        loginData?.must_complete_onboarding ??
        checkData?.user?.must_complete_onboarding
      );

      // Keep AuthContext in sync so /edit/complete-account (and ProtectedRoute) see the session.
      writeAuthProfileCache({
        id: loginData?.user_id ?? checkData?.user?.id,
        username: loginData?.username ?? checkData?.user?.username,
        must_complete_onboarding: mustCompleteOnboarding,
      });
      setAuth({
        userId: loginData?.user_id ?? checkData?.user?.id,
        username: loginData?.username ?? checkData?.user?.username,
        mustCompleteOnboarding,
      });

      if (mustCompleteOnboarding) {
        navigate(`/edit/complete-account?returnTo=${encodeURIComponent(dashboardPath)}`);
        return;
      }

      navigate(dashboardPath);
    } catch (err) {
      const status = err?.status;
      if (status === 403 && isAffiliatePortalBlockedError(err)) {
        setPortalBlockedMessage(getAffiliatePortalBlockedMessage(err));
        setError("");
      } else if (status === 403)
        setError(
          isPartnerPath
            ? "This account does not have portal access."
            : "This account is not an affiliate."
        );
      else if (err?.message === "Network error" || err?.name === "TypeError") {
        setIsNetworkError(true);
        setError("Network error. Please try again.");
      } else setError(err?.message || "Login failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome to"
      subtitle={
        showPortalBlocked
          ? undefined
          : `Sign in to the ${portalName} to view your coupons and analytics`
      }
      navClassName={affiliateNavbarGradient}
      logoIconColor="#ffffff"
      logoTextColor="text-white"
      domainClassName={affiliateBrandGradientText}
      backButtonClassName="text-white/80 hover:text-white"
      themeToggleMoonColor="text-white"
      themeToggleSunColor="text-white"
      themeToggleMoonHoverFill="hover:fill-white"
      themeToggleClassName="hover:bg-white/10"
    >
      {showPortalBlocked ? (
        <AffiliatePortalBlockedNotice message={blockedMessage} />
      ) : (
        <>
      <h2 className={`${typography.h2} ${textColors.primary} mb-6 text-center`}>
        {isPartnerPath ? "Partner Sign In" : "Affiliate Sign In"}
      </h2>
      {isNetworkError ? (
        <div className="mb-4">
          <AuthNetworkErrorState
            title="Network error :("
            description="We couldn't sign you in right now. Please check your connection."
            className="py-6"
          />
        </div>
      ) : error ? (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-center">
          {error}
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-6">
        <FormField label="Email or Username" htmlFor="affiliate-email" required error={emailError}>
          <Input
            id="affiliate-email"
            type="text"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            placeholder="Enter your email or username"
            required
            disabled={isSubmitting}
          />
        </FormField>

        <FormField label="Password" htmlFor="affiliate-password" required error={passwordError}>
          <Input
            id="affiliate-password"
            type={isPasswordVisible ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) setPasswordError("");
            }}
            placeholder="Enter your password"
            rightAdornment={
              <button
                type="button"
                onMouseDown={(e) => {
                  // Prevent input blur when toggling via mouse
                  e.preventDefault();
                }}
                onClick={() => setIsPasswordVisible((v) => !v)}
                aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                aria-pressed={isPasswordVisible}
                className={`h-9 w-9 inline-flex items-center justify-center rounded-md transition-colors ${textColors.muted} ${textColors.hover} focus:outline-none focus:ring-2 focus:ring-pink-400 dark:focus:ring-pink-300`}
              >
                {isPasswordVisible ? <EyeOffIcon size="sm" /> : <EyeIcon size="sm" />}
              </button>
            }
            required
            disabled={isSubmitting}
          />
          <div className="mt-2 text-right">
            <Link
              to={`/edit/recovery?returnTo=${basePath}/login`}
              className={`${typography.bodySmall} underline font-medium transition-colors text-purple-800 dark:text-pink-300 hover:text-pink-700 dark:hover:text-pink-200`}
            >
              Forgot password?
            </Link>
          </div>
        </FormField>

        <AffiliatePrimaryButton type="submit" fullWidth loading={isSubmitting}>
          Sign In
        </AffiliatePrimaryButton>
      </form>

      <div className="mt-6">
        <div className="flex items-center gap-4">
          <div className={`flex-1 border-t ${borders.default}`} />
          <span className={`${typography.muted} ${textColors.tertiary}`}>or</span>
          <div className={`flex-1 border-t ${borders.default}`} />
        </div>

        <div className="mt-6">
          <GoogleAuthButton
            mode="login"
            disabled={isSubmitting}
            onClick={() => {
              // Marker read by GoogleAuthCallback to route back into the
              // portal (on the same prefix) after Google authentication completes.
              sessionStorage.setItem("googleAuthReturnTo", isPartnerPath ? "partner" : "affiliate");
            }}
          />
        </div>
      </div>

      <p className={`${typography.caption} ${textColors.secondary} mt-6 text-center`}>
        By continuing, you agree to our{" "}
        <Link
          to="/terms"
          target="_blank"
          rel="noopener noreferrer"
          className={`underline font-medium ${interactive.link} ${interactive.linkHover}`}
        >
          Terms of Use
        </Link>{" "}
        and acknowledge our{" "}
        <Link
          to="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className={`underline font-medium ${interactive.link} ${interactive.linkHover}`}
        >
          Privacy Policy
        </Link>
        .
      </p>
        </>
      )}
    </AuthLayout>
  );
};

export default AffiliateLogin;
